import { isValidOutput, isValidRecursiveQuestion } from "./blockValidation";
import checkIfBlockIsVisible from "./checkIfBlockIsVisible";
import checkIfStepIsVisible from "./checkIfStepIsVisible";

import { getFixedProductInCalculation } from "./prepareConfigurationStep";

const getStep = (context, stepId) => context.data.find((s) => s.id === stepId);

const getContextEntityListKey = (entity) => {
  switch (entity) {
    case 'product':
      return 'products'
    case 'system':
      return 'systems';
    default:
      throw Error(`Unsupported entity '${entity}'`);
  }
}

export const getEntitiesFromResponses = (responses, role, entity) => {
  return Object.values(responses)
    .reduce((acc, response) => {
      if (response.recursive_question_value?.length) {
        return [
          ...acc,
          ...response.recursive_question_value.reduce((ac, cursiveResponse) => {
            return [...ac, ...Object.values(cursiveResponse)];
          }, []),
        ];
      }
      return [...acc, response];
    }, [])
    .reduce(
      (prev, r) => {
        if (!!r?.value?.[entity])
          // direct entity selected (for handling k-color)
          return [...prev, r.value[entity]];
        if (!!r?.option?.[entity])
          // single choice selector
          return [...prev, r.option[entity]];
        if (Array.isArray(r.value) && r?.question?.options) {
          // multiple choice selector
          const selectedIds = r.value.map((eid) => parseInt(eid));
          return [
            ...prev,
            ...r.question.options
              .filter((o) => selectedIds.includes(o.id) && o[entity])
              .map((o) => o[entity])
          ]
        }
        return prev;
      },
      []
    )
};

const getFixedEntities = (context, role, entity) => {
  const allOutputs = context.configuration.filter((b) => {
    const step = getStep(context, b.step_id);
    if (
      b.type === "output" &&
      b.output_type === "calculation" &&
      !b.recursive_question_id &&
      !(b.hidden_question?.length && b.hidden_question.indexOf(`role_type_${role}`) >= 0) &&
      checkIfBlockIsVisible(b, context) &&
      checkIfStepIsVisible(step, context, true)
    ) {
      return true;
    }
    return false;
  });

  const fixedEntities = getFixedProductInCalculation(allOutputs);

  const contextListKey = getContextEntityListKey(entity);
  if (fixedEntities?.length && context?.[contextListKey]) {
    return fixedEntities
      .filter((id) => context?.[contextListKey]?.[id])
      .map((id) => context?.[contextListKey]?.[id]);
  }

  return [];
};

const getFixedRecursiveEntitiesInCalculation = (context, role, entity) => {
  // retrieve all recursive question visible and with at least one response
  const allRecursiveQuestion = context.configuration
    .filter((b) => isValidRecursiveQuestion(b, context))
    .filter((b) => {
      const step = getStep(context, b.step_id);
      if (
        context.responses?.[b.id]?.recursive_question_value?.length &&
        checkIfStepIsVisible(step, context, true) &&
        !(b.hidden_question?.length && b.hidden_question.indexOf(`role_type_${role}`) >= 0) &&
        checkIfBlockIsVisible(b, context)
      ) {
        return true;
      }
      return false;
    });

  if (!allRecursiveQuestion?.length) {
    return [];
  }

  const entities = allRecursiveQuestion.reduce((acc, b) => {
    const responses = context.responses?.[b.id]?.recursive_question_value;
    // retrieve all recursive question calculation outputs
    const outputs = context.configuration.filter(
      (block) =>
        block.recursive_question_id === b.id &&
        isValidOutput(block) &&
        !(block.hidden_question?.length && block.hidden_question.indexOf(`role_type_${role}`) >= 0) &&
        block.output_type === "calculation"
    );

    // get all recursive question's responses and all output with a calculation

    return [
      ...acc,
      ...responses.reduce((ac, response) => {
        // filter all output with confingurator and recursive question responses
        const visibleOutputs = outputs.filter((output) =>
          checkIfBlockIsVisible(output, {
            ...context,
            responses: { ...context.responses, ...response },
          })
        );

        if (!visibleOutputs.length) {
          return ac;
        }

        const entities = getFixedProductInCalculation(visibleOutputs);
        return [...ac, ...entities];
      }, []),
    ];
  }, []);

  const contextListKey = getContextEntityListKey(entity);
  if (entities?.length && context?.[contextListKey]) {
    return entities
      .filter((id) => context?.[contextListKey]?.[id])
      .map((id) => context?.[contextListKey]?.[id]);
  }
  return [];
};

const getAllConfigurationEntities = (context, role, entity) => {
  // looking for a K-Color product only if entity is 'product',(we look for K-Color products in responses instead of in kcolorresults)
  const kColorProductsFromResponses = entity === 'product'
    ? getEntitiesFromResponses(context.responses, role, 'kColorProduct').map((kcp) => {
        if (!context.kColorProducts?.length) return kcp;
        // Replace with the enriched product from context, matched by id and cod_prod_comm
        return context.kColorProducts.find(
          (p) => p.id === kcp.id && p.cod_prod_comm === kcp.cod_prod_comm
        ) || kcp;
      })
    : [];
  const entities = [
    ...getEntitiesFromResponses(context.responses, role, entity),
    ...kColorProductsFromResponses,
    ...getFixedEntities(context, role, entity),
    ...getFixedRecursiveEntitiesInCalculation(context, role, entity),
  ];
  if (entities.length) {
    return [...new Map(entities.map((v) => [v.id, v])).values()];
  }

  return [];
};

export const getAllConfigurationProducts = (context, role) => getAllConfigurationEntities(context, role, 'product');
export const getAllConfigurationSystems = (context, role) => getAllConfigurationEntities(context, role, 'system');
